import { Routes } from '@angular/router';

import { phoenixUnsavedChangesGuard } from '../core/guards/unsaved-changes.guard';

/**
 * CHILD routes of one environment workspace (`/backup-environments/{agentId}`, AB#2499 wave W1a).
 *
 * Exported as its own constant for two reasons: `app.routes.ts` stays readable, and the routing specs
 * can exercise the REAL child table (paths, order, redirects, guard placement, `data.section`) instead
 * of a hand-copied double that could drift away from it.
 *
 * DECISIONS PINNED HERE:
 *  • `data.section` is the section vocabulary — the shell reads it to highlight the tab, so the drill-in
 *    `configuration/observed` keeps the Configuration tab active WITHOUT any URL string matching.
 *  • The unsaved-changes guard sits on the CHILD that hosts the config form — since AB#2500 that is the
 *    `configuration` route — and never on the shell: a parent `canDeactivate` does NOT fire when the
 *    user switches to a SIBLING section, so a guard up there would silently drop a dirty form on a tab
 *    click. On the child it covers every case (a sibling switch deactivates the child, including the
 *    drill-in to `configuration/observed`, and so does leaving the workspace). The guard travels with
 *    the FORM, not with a fixed section: it moved off `overview` in the same commit the form did.
 *  • The empty path REDIRECTS instead of rendering: `/backup-environments/{agentId}` — every pre-W1a link,
 *    bookmark and mail — lands on the overview section. Its FRAGMENT survives the redirect, which used
 *    to matter for `#env-config`; that anchor moved to the configuration section with the form
 *    (AB#2500), so the setup CTA now addresses `configuration#env-config` directly and a legacy
 *    fragment simply finds nothing to scroll to instead of breaking anything.
 *  • The pre-W1a `/backup-environments/{agentId}/observed` address redirects to the drift view's new home
 *    under the configuration section.
 *  • The trailing wildcard keeps an unknown/typo'd section inside the workspace (→ overview) instead of
 *    letting the app-level `**` throw the customer out to the landing page.
 */
export const ENVIRONMENT_WORKSPACE_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'overview' },
  {
    // OVERVIEW — the default section. Every axis has now left it: the export one with AB#2502 (W1d),
    // the configuration form with AB#2500 (W1b) and recovery with AB#2501 (W1c). It stays a REAL route
    // (the tab bar's home, the target of the bare-address redirect and of every legacy bookmark) and
    // receives its summary content in AB#2503 (W2). NO leave guard: it went with the form to the
    // `configuration` route below, because a guard is only meaningful on the route whose component owns
    // the dirty state.
    path: 'overview',
    data: { section: 'overview', title: 'phoenix.environments.detail.title' },
    loadComponent: () =>
      import('./environment-overview-page.component').then((m) => m.EnvironmentOverviewPageComponent),
  },
  {
    // EXPORT — agent, config plausibility and host probes as separate axes, plus the configuration test
    // (AB#2502, wave W1d). NO leave guard: the section has no dirty-able input, every control commits on
    // click. The running-test state it shows lives in the shell-provided store, so it survives coming back
    // here — that is a property of where the state lives, not of this route.
    path: 'export',
    data: { section: 'export', title: 'phoenix.environments.workspace.tab.export' },
    loadComponent: () =>
      import('./environment-export-page.component').then((m) => m.EnvironmentExportPageComponent),
  },
  {
    // RECOVERY — the temporary Wiederherstellungsumgebung FIRST, the permanent Recovery-Builder after it
    // (AB#2501, wave W1c). NO leave guard: nothing here is dirty-able, every control commits on click
    // behind its own confirmation. This section's two POLLS are route-local — its component binds them
    // to its own lifetime through the store's `enterRecoverySection`/`leaveRecoverySection`, so leaving
    // the tab genuinely stops them while the snapshots and any committed operation survive.
    path: 'recovery',
    data: { section: 'recovery', title: 'phoenix.environments.workspace.tab.recovery' },
    loadComponent: () =>
      import('./environment-recovery-page.component').then((m) => m.EnvironmentRecoveryPageComponent),
  },
  {
    // CONFIGURATION/OBSERVED — configured-vs-observed drift. Declared BEFORE the bare `configuration`
    // path so the more specific address is matched first regardless of future path shapes.
    // It is a SIBLING of `configuration`, not a child of it: opening the drift view therefore
    // DEACTIVATES the editor route, which is exactly what makes the leave guard below fire for this
    // drill-in too. NO guard of its own — the drift view is read-only.
    path: 'configuration/observed',
    data: { section: 'configuration', title: 'phoenix.config.drift.title' },
    loadComponent: () =>
      import('../config/config-observed-page.component').then((m) => m.ConfigObservedPageComponent),
  },
  {
    // CONFIGURATION — the bound config's editor (AB#2500 wave W1b, together with the one-config cut
    // AB#2676). It HOSTS the config-authoring form, so it is the route that carries the leave guard.
    path: 'configuration',
    data: { section: 'configuration', title: 'phoenix.environments.workspace.tab.configuration' },
    canDeactivate: [phoenixUnsavedChangesGuard],
    loadComponent: () =>
      import('./environment-configuration-page.component').then(
        (m) => m.EnvironmentConfigurationPageComponent
      ),
  },
  // Legacy address of the drift view (pre-W1a it was a sibling of the detail page).
  { path: 'observed', pathMatch: 'full', redirectTo: 'configuration/observed' },
  // Unknown section → the default one, inside the workspace.
  { path: '**', redirectTo: 'overview' },
];
